import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const FinancialPerformancePanel = ({ organizationId, refreshTrigger }) => {
  const { userProfile, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('12months');
  const [performanceData, setPerformanceData] = useState({
    revenueGrowth: [],
    profitability: [],
    expenseBreakdown: [],
    kpiMetrics: {},
    comparativeAnalysis: {}
  });

  useEffect(() => {
    if (isAuthenticated && (organizationId || userProfile?.organization_id)) {
      loadFinancialPerformanceData();
    }
  }, [isAuthenticated, organizationId, userProfile?.organization_id, refreshTrigger, timeRange]);

  const loadFinancialPerformanceData = async () => {
    const orgId = organizationId || userProfile?.organization_id;
    if (!orgId) return;

    try {
      setLoading(true);
      setError(null);

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      
      switch (timeRange) {
        case '6months':
          startDate?.setMonth(endDate?.getMonth() - 6);
          break;
        case '24months':
          startDate?.setMonth(endDate?.getMonth() - 24);
          break;
        default: // 12months
          startDate?.setMonth(endDate?.getMonth() - 12);
      }

      // Parallel data fetching for better performance
      const [
        invoicesResponse,
        billsResponse,
        paymentsResponse,
        journalEntriesResponse,
        accountBalancesResponse
      ] = await Promise.all([
        // Revenue data from invoices
        supabase
          ?.from('invoices')
          ?.select('total_amount, subtotal, tax_amount, status, issue_date, created_at, amount_paid, customer_id')
          ?.eq('organization_id', orgId)
          ?.gte('issue_date', startDate?.toISOString()?.split('T')?.[0])
          ?.lte('issue_date', endDate?.toISOString()?.split('T')?.[0]),

        // Expense data from bills
        supabase
          ?.from('bills')
          ?.select('total_amount, subtotal, tax_amount, status, issue_date, created_at, amount_paid, vendor_id')
          ?.eq('organization_id', orgId)
          ?.gte('issue_date', startDate?.toISOString()?.split('T')?.[0])
          ?.lte('issue_date', endDate?.toISOString()?.split('T')?.[0]),

        // Cash flow data from payments
        supabase
          ?.from('payments_received')
          ?.select('amount_received, payment_date, status, payment_method, customer_id')
          ?.eq('organization_id', orgId)
          ?.gte('payment_date', startDate?.toISOString()?.split('T')?.[0])
          ?.lte('payment_date', endDate?.toISOString()?.split('T')?.[0]),

        // Additional transaction data from journal entries
        supabase
          ?.from('journal_entries')
          ?.select(`
            entry_date,
            total_debit,
            total_credit,
            status,
            journal_entry_lines (
              debit_amount,
              credit_amount,
              chart_of_accounts (
                account_type,
                account_subtype,
                account_name
              )
            )
          `)
          ?.eq('organization_id', orgId)
          ?.eq('status', 'posted')
          ?.gte('entry_date', startDate?.toISOString()?.split('T')?.[0])
          ?.lte('entry_date', endDate?.toISOString()?.split('T')?.[0]),

        // Historical account balances for trend analysis
        supabase
          ?.from('account_balances')
          ?.select('closing_balance, period_month, period_year, account_id, chart_of_accounts(account_type, account_name)')
          ?.eq('organization_id', orgId)
          ?.eq('is_period_closed', true)
          ?.gte('period_year', startDate?.getFullYear())
      ]);

      // Handle any errors
      const responses = [invoicesResponse, billsResponse, paymentsResponse, journalEntriesResponse, accountBalancesResponse];
      const errors = responses?.filter(response => response?.error);
      
      if (errors?.length > 0) {
        console.error('Errors loading financial performance data:', errors);
        setError('Failed to load some financial data');
      }

      // Extract data
      const invoices = invoicesResponse?.data || [];
      const bills = billsResponse?.data || [];
      const payments = paymentsResponse?.data || [];
      const journalEntries = journalEntriesResponse?.data || [];
      const accountBalances = accountBalancesResponse?.data || [];

      // Process and analyze the data
      const analysisResults = analyzeFinancialPerformance(
        invoices, bills, payments, journalEntries, accountBalances, startDate, endDate
      );

      setPerformanceData(analysisResults);

    } catch (err) {
      console.error('Error loading financial performance data:', err);
      setError('Failed to load financial performance data');
    } finally {
      setLoading(false);
    }
  };

  const analyzeFinancialPerformance = (invoices, bills, payments, journalEntries, accountBalances, startDate, endDate) => {
    // Monthly Revenue Growth Analysis
    const revenueGrowth = calculateMonthlyRevenue(invoices, startDate, endDate);
    
    // Profitability Analysis
    const profitability = calculateProfitability(invoices, bills, startDate, endDate);
    
    // Expense Breakdown Analysis
    const expenseBreakdown = analyzeExpenseBreakdown(bills, journalEntries);
    
    // Key Performance Indicators
    const kpiMetrics = calculateKPIMetrics(invoices, bills, payments);
    
    // Comparative Analysis (current vs previous period)
    const comparativeAnalysis = performComparativeAnalysis(invoices, bills, payments, startDate);

    return {
      revenueGrowth,
      profitability,
      expenseBreakdown,
      kpiMetrics,
      comparativeAnalysis
    };
  };

  const calculateMonthlyRevenue = (invoices, startDate, endDate) => {
    const monthlyMap = new Map();
    
    // Initialize months
    const current = new Date(startDate);
    while (current <= endDate) {
      const monthKey = `${current?.getFullYear()}-${String(current?.getMonth() + 1)?.padStart(2, '0')}`;
      const monthName = current?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      monthlyMap?.set(monthKey, {
        month: monthName,
        revenue: 0,
        growth: 0,
        invoiceCount: 0
      });
      
      current?.setMonth(current?.getMonth() + 1);
    }

    // Process invoices
    invoices?.forEach(invoice => {
      const date = new Date(invoice?.issue_date);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      
      if (monthlyMap?.has(monthKey)) {
        const monthData = monthlyMap?.get(monthKey);
        if (invoice?.status === 'paid' || invoice?.status === 'partially_paid') {
          monthData.revenue += Number(invoice?.total_amount || 0);
          monthData.invoiceCount += 1;
        }
        monthlyMap?.set(monthKey, monthData);
      }
    });

    // Calculate growth rates
    const monthlyArray = Array?.from(monthlyMap?.values());
    for (let i = 1; i < monthlyArray?.length; i++) {
      const current = monthlyArray?.[i];
      const previous = monthlyArray?.[i - 1];
      
      if (previous?.revenue > 0) {
        current.growth = ((current?.revenue - previous?.revenue) / previous?.revenue) * 100;
      }
    }

    return monthlyArray;
  };

  const calculateProfitability = (invoices, bills, startDate, endDate) => {
    const monthlyMap = new Map();
    
    // Initialize months
    const current = new Date(startDate);
    while (current <= endDate) {
      const monthKey = `${current?.getFullYear()}-${String(current?.getMonth() + 1)?.padStart(2, '0')}`;
      const monthName = current?.toLocaleDateString('en-US', { month: 'short' });
      
      monthlyMap?.set(monthKey, {
        month: monthName,
        revenue: 0,
        expenses: 0,
        grossProfit: 0,
        margin: 0
      });
      
      current?.setMonth(current?.getMonth() + 1);
    }

    // Process revenue
    invoices?.forEach(invoice => {
      const date = new Date(invoice?.issue_date);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      
      if (monthlyMap?.has(monthKey) && (invoice?.status === 'paid' || invoice?.status === 'partially_paid')) {
        const monthData = monthlyMap?.get(monthKey);
        monthData.revenue += Number(invoice?.total_amount || 0);
        monthlyMap?.set(monthKey, monthData);
      }
    });

    // Process expenses
    bills?.forEach(bill => {
      const date = new Date(bill?.issue_date);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      
      if (monthlyMap?.has(monthKey) && (bill?.status === 'paid' || bill?.status === 'approved')) {
        const monthData = monthlyMap?.get(monthKey);
        monthData.expenses += Number(bill?.total_amount || 0);
        monthlyMap?.set(monthKey, monthData);
      }
    });

    // Calculate profitability metrics
    return Array?.from(monthlyMap?.values())?.map(data => ({
      ...data,
      grossProfit: data?.revenue - data?.expenses,
      margin: data?.revenue > 0 ? ((data?.revenue - data?.expenses) / data?.revenue) * 100 : 0
    }));
  };

  const analyzeExpenseBreakdown = (bills, journalEntries) => {
    const expenseCategories = new Map();
    
    // Analyze bills by vendor/category
    bills?.forEach(bill => {
      if (bill?.status === 'paid' || bill?.status === 'approved') {
        const amount = Number(bill?.total_amount || 0);
        const category = `Vendor Payments`; // Could be enhanced with vendor categorization
        
        if (expenseCategories?.has(category)) {
          expenseCategories?.set(category, expenseCategories?.get(category) + amount);
        } else {
          expenseCategories?.set(category, amount);
        }
      }
    });

    // Analyze journal entries by account type
    journalEntries?.forEach(entry => {
      entry?.journal_entry_lines?.forEach(line => {
        const account = line?.chart_of_accounts;
        if (account?.account_type === 'expense' && line?.debit_amount > 0) {
          const category = account?.account_subtype || account?.account_name || 'Other Expenses';
          const amount = Number(line?.debit_amount);
          
          if (expenseCategories?.has(category)) {
            expenseCategories?.set(category, expenseCategories?.get(category) + amount);
          } else {
            expenseCategories?.set(category, amount);
          }
        }
      });
    });

    // Convert to array and calculate percentages
    const totalExpenses = Array?.from(expenseCategories?.values())?.reduce((sum, amount) => sum + amount, 0);
    
    return Array?.from(expenseCategories?.entries())?.map(([name, value]) => ({
      name: name?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase()),
      value,
      percentage: totalExpenses > 0 ? (value / totalExpenses) * 100 : 0
    }))?.sort((a, b) => b?.value - a?.value)?.slice(0, 8); // Top 8 categories
  };

  const calculateKPIMetrics = (invoices, bills, payments) => {
    // Revenue metrics
    const totalRevenue = invoices?.reduce((sum, inv) => {
      return sum + (inv?.status === 'paid' ? Number(inv?.total_amount || 0) : 0);
    }, 0);

    const totalExpenses = bills?.reduce((sum, bill) => {
      return sum + (bill?.status === 'paid' ? Number(bill?.total_amount || 0) : 0);
    }, 0);

    // Outstanding amounts
    const outstandingInvoices = invoices?.reduce((sum, inv) => {
      return sum + (['sent', 'viewed', 'overdue']?.includes(inv?.status) ? Number(inv?.total_amount || 0) : 0);
    }, 0);

    const outstandingBills = bills?.reduce((sum, bill) => {
      return sum + (['pending_approval', 'approved']?.includes(bill?.status) ? Number(bill?.total_amount || 0) : 0);
    }, 0);

    // Payment metrics
    const totalPaymentsReceived = payments?.reduce((sum, payment) => {
      return sum + (payment?.status === 'cleared' ? Number(payment?.amount_received || 0) : 0);
    }, 0);

    // Calculate derived metrics
    const netProfit = totalRevenue - totalExpenses;
    const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
    const cashConversion = totalRevenue > 0 ? (totalPaymentsReceived / totalRevenue) * 100 : 0;

    // Average transaction values
    const avgInvoiceValue = invoices?.length > 0 ? totalRevenue / invoices?.filter(inv => inv?.status === 'paid')?.length : 0;
    const avgPaymentValue = payments?.length > 0 ? totalPaymentsReceived / payments?.filter(p => p?.status === 'cleared')?.length : 0;

    return {
      totalRevenue,
      totalExpenses,
      netProfit,
      profitMargin,
      outstandingInvoices,
      outstandingBills,
      totalPaymentsReceived,
      cashConversion,
      avgInvoiceValue,
      avgPaymentValue,
      totalInvoices: invoices?.length,
      totalBills: bills?.length,
      totalPayments: payments?.length
    };
  };

  const performComparativeAnalysis = (invoices, bills, payments, currentStartDate) => {
    // Calculate previous period dates
    const currentEndDate = new Date();
    const periodLength = currentEndDate?.getTime() - currentStartDate?.getTime();
    const previousEndDate = new Date(currentStartDate?.getTime() - 1);
    const previousStartDate = new Date(previousEndDate?.getTime() - periodLength);

    // Filter data for previous period
    const previousInvoices = invoices?.filter(inv => {
      const date = new Date(inv?.issue_date);
      return date >= previousStartDate && date <= previousEndDate;
    });

    const previousBills = bills?.filter(bill => {
      const date = new Date(bill?.issue_date);
      return date >= previousStartDate && date <= previousEndDate;
    });

    const previousPayments = payments?.filter(payment => {
      const date = new Date(payment?.payment_date);
      return date >= previousStartDate && date <= previousEndDate;
    });

    // Calculate current period metrics
    const currentMetrics = calculateKPIMetrics(invoices, bills, payments);
    const previousMetrics = calculateKPIMetrics(previousInvoices, previousBills, previousPayments);

    // Calculate percentage changes
    const calculateChange = (current, previous) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    };

    return {
      revenueChange: calculateChange(currentMetrics?.totalRevenue, previousMetrics?.totalRevenue),
      expenseChange: calculateChange(currentMetrics?.totalExpenses, previousMetrics?.totalExpenses),
      profitChange: calculateChange(currentMetrics?.netProfit, previousMetrics?.netProfit),
      marginChange: currentMetrics?.profitMargin - previousMetrics?.profitMargin,
      invoiceCountChange: calculateChange(currentMetrics?.totalInvoices, previousMetrics?.totalInvoices),
      paymentEfficiencyChange: currentMetrics?.cashConversion - previousMetrics?.cashConversion,
      avgInvoiceChange: calculateChange(currentMetrics?.avgInvoiceValue, previousMetrics?.avgInvoiceValue)
    };
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-muted-foreground">{entry?.name}:</span>
              <span className="font-medium text-foreground">
                {entry?.name?.includes('Margin') || entry?.name?.includes('Growth') 
                  ? `${entry?.value?.toFixed(1)}%` 
                  : `$${entry?.value?.toLocaleString()}`}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const timeRangeOptions = [
    { value: '6months', label: 'Last 6 Months' },
    { value: '12months', label: 'Last 12 Months' },
    { value: '24months', label: 'Last 24 Months' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'revenue', label: 'Revenue Growth', icon: 'TrendingUp' },
    { id: 'profitability', label: 'Profitability', icon: 'DollarSign' },
    { id: 'expenses', label: 'Expense Analysis', icon: 'PieChart' }
  ];

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-professional">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Financial Performance Analysis</h2>
          <p className="text-sm text-muted-foreground mt-1">Loading performance data...</p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center h-96">
            <div className="flex items-center space-x-3">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              <span className="text-lg text-muted-foreground">Analyzing financial performance...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-professional">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Financial Performance Analysis</h2>
          <p className="text-sm text-error mt-1">{error}</p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Icon name="AlertCircle" size={24} className="text-error mx-auto mb-4" />
              <Button onClick={loadFinancialPerformanceData} variant="outline">
                <Icon name="RefreshCw" size={16} />
                Retry Analysis
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-professional">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Financial Performance Analysis</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Comprehensive analysis with live data and comparative insights
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Select
              options={timeRangeOptions}
              value={timeRange}
              onChange={setTimeRange}
              className="w-40"
            />
            <Button 
              variant="ghost" 
              size="sm"
              onClick={loadFinancialPerformanceData}
              disabled={loading}
            >
              <Icon name="RefreshCw" size={16} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mt-4 bg-muted/50 rounded-lg p-1">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Total Revenue</p>
                    <p className="text-2xl font-bold text-blue-900">
                      ${performanceData?.kpiMetrics?.totalRevenue?.toLocaleString()}
                    </p>
                  </div>
                  <Icon name="DollarSign" size={24} className="text-blue-600" />
                </div>
                <div className="mt-2 flex items-center">
                  <Icon 
                    name={performanceData?.comparativeAnalysis?.revenueChange >= 0 ? 'TrendingUp' : 'TrendingDown'} 
                    size={16} 
                    className={performanceData?.comparativeAnalysis?.revenueChange >= 0 ? 'text-green-600' : 'text-red-600'} 
                  />
                  <span className={`text-sm ml-1 ${performanceData?.comparativeAnalysis?.revenueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {performanceData?.comparativeAnalysis?.revenueChange?.toFixed(1)}% vs prev period
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Net Profit</p>
                    <p className="text-2xl font-bold text-green-900">
                      ${performanceData?.kpiMetrics?.netProfit?.toLocaleString()}
                    </p>
                  </div>
                  <Icon name="TrendingUp" size={24} className="text-green-600" />
                </div>
                <div className="mt-2 flex items-center">
                  <span className="text-sm text-green-600 font-medium">
                    {performanceData?.kpiMetrics?.profitMargin?.toFixed(1)}% margin
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 font-medium">Outstanding</p>
                    <p className="text-2xl font-bold text-orange-900">
                      ${performanceData?.kpiMetrics?.outstandingInvoices?.toLocaleString()}
                    </p>
                  </div>
                  <Icon name="Clock" size={24} className="text-orange-600" />
                </div>
                <div className="mt-2">
                  <span className="text-sm text-orange-600">
                    {performanceData?.kpiMetrics?.cashConversion?.toFixed(1)}% collection rate
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Avg Invoice</p>
                    <p className="text-2xl font-bold text-purple-900">
                      ${performanceData?.kpiMetrics?.avgInvoiceValue?.toLocaleString()}
                    </p>
                  </div>
                  <Icon name="Receipt" size={24} className="text-purple-600" />
                </div>
                <div className="mt-2">
                  <span className="text-sm text-purple-600">
                    {performanceData?.kpiMetrics?.totalInvoices} invoices
                  </span>
                </div>
              </div>
            </div>

            {/* Revenue vs Profit Chart */}
            <div className="bg-muted/20 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">Revenue vs Profitability Trend</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={performanceData?.profitability}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={12} />
                    <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#3b82f6" 
                      strokeWidth={3}
                      name="Revenue"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="grossProfit" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      name="Gross Profit"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'revenue' && (
          <div className="space-y-6">
            <div className="bg-muted/20 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">Monthly Revenue Growth</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={performanceData?.revenueGrowth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={12} />
                    <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-muted/20 rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-4">Growth Rate Trend</h4>
                <div className="h-60">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={performanceData?.revenueGrowth}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={12} />
                      <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line 
                        type="monotone" 
                        dataKey="growth" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        name="Growth Rate"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-foreground">Revenue Insights</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <span className="text-sm text-muted-foreground">Best Month</span>
                    <span className="font-medium text-foreground">
                      {performanceData?.revenueGrowth?.reduce((best, current) => 
                        current?.revenue > (best?.revenue || 0) ? current : best, {})?.month || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <span className="text-sm text-muted-foreground">Avg Monthly Revenue</span>
                    <span className="font-medium text-foreground">
                      ${(performanceData?.revenueGrowth?.reduce((sum, month) => sum + month?.revenue, 0) / (performanceData?.revenueGrowth?.length || 1))?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <span className="text-sm text-muted-foreground">Total Invoices</span>
                    <span className="font-medium text-foreground">
                      {performanceData?.kpiMetrics?.totalInvoices}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'profitability' && (
          <div className="space-y-6">
            <div className="bg-muted/20 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">Profit Margin Analysis</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={performanceData?.profitability}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={12} />
                    <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="margin" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      name="Profit Margin"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-muted/20 rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-4">Revenue vs Expenses</h4>
                <div className="h-60">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={performanceData?.profitability}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={12} />
                      <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                      <Bar dataKey="expenses" fill="#ef4444" name="Expenses" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-foreground">Profitability Metrics</h4>
                <div className="space-y-3">
                  <div className="p-3 bg-background rounded-lg">
                    <div className="text-sm text-muted-foreground">Current Margin</div>
                    <div className="text-xl font-bold text-green-600">
                      {performanceData?.kpiMetrics?.profitMargin?.toFixed(1)}%
                    </div>
                  </div>
                  <div className="p-3 bg-background rounded-lg">
                    <div className="text-sm text-muted-foreground">Best Margin</div>
                    <div className="text-lg font-semibold text-foreground">
                      {Math.max(...(performanceData?.profitability?.map(p => p?.margin) || [0]))?.toFixed(1)}%
                    </div>
                  </div>
                  <div className="p-3 bg-background rounded-lg">
                    <div className="text-sm text-muted-foreground">Avg Margin</div>
                    <div className="text-lg font-semibold text-foreground">
                      {(performanceData?.profitability?.reduce((sum, p) => sum + p?.margin, 0) / (performanceData?.profitability?.length || 1))?.toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'expenses' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-muted/20 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-foreground mb-4">Expense Breakdown</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={performanceData?.expenseBreakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percentage }) => `${name}: ${percentage?.toFixed(1)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {performanceData?.expenseBreakdown?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-foreground">Top Expense Categories</h4>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {performanceData?.expenseBreakdown?.map((expense, index) => (
                    <div key={expense?.name} className="flex items-center justify-between p-3 bg-background rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
                        />
                        <span className="text-sm font-medium text-foreground">{expense?.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-foreground">
                          ${expense?.value?.toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {expense?.percentage?.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-muted/20 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-4">Expense Analysis Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-background rounded-lg">
                  <Icon name="DollarSign" size={24} className="text-red-500 mx-auto mb-2" />
                  <div className="text-sm text-muted-foreground">Total Expenses</div>
                  <div className="text-xl font-bold text-foreground">
                    ${performanceData?.kpiMetrics?.totalExpenses?.toLocaleString()}
                  </div>
                </div>
                <div className="text-center p-4 bg-background rounded-lg">
                  <Icon name="TrendingDown" size={24} className="text-orange-500 mx-auto mb-2" />
                  <div className="text-sm text-muted-foreground">Expense Change</div>
                  <div className={`text-xl font-bold ${performanceData?.comparativeAnalysis?.expenseChange >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {performanceData?.comparativeAnalysis?.expenseChange >= 0 ? '+' : ''}{performanceData?.comparativeAnalysis?.expenseChange?.toFixed(1)}%
                  </div>
                </div>
                <div className="text-center p-4 bg-background rounded-lg">
                  <Icon name="PieChart" size={24} className="text-blue-500 mx-auto mb-2" />
                  <div className="text-sm text-muted-foreground">Categories</div>
                  <div className="text-xl font-bold text-foreground">
                    {performanceData?.expenseBreakdown?.length}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data source indicator */}
        <div className="mt-6 pt-4 border-t border-border">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Icon name="Database" size={12} />
              <span>Live analysis from Supabase • Invoices, Bills, Payments & Journal Entries</span>
              <span>•</span>
              <span>Last updated: {new Date()?.toLocaleTimeString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialPerformancePanel;